import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
  BookOpen, 
  Search, 
  Sparkles, 
  Settings, 
  CreditCard, 
  ShieldCheck, 
  HelpCircle, 
  Clock, 
  ArrowRight, 
  ChevronRight, 
  ChevronDown, 
  ExternalLink,
  ShieldAlert,
  SlidersHorizontal,
  X,
  FileText,
  Menu,
  ArrowUp,
  Compass,
  Map,
  GraduationCap,
  Info,
  Phone,
  User,
  Mail,
  Lock,
  MessageSquare,
  AlertTriangle
} from "lucide-react";
import { useTranslation } from "../utils/i18n";
import { SeoBreadcrumbs } from "./seo/SeoBreadcrumbs";

interface DocumentationPageProps {
  onUpgradeClick?: () => void;
  onOpenSettingsClick?: () => void;
  onOpenLegalTab?: (tab: any) => void;
  onMenuClick?: () => void;
  isProUser?: boolean;
  onContactSupportClick?: () => void;
  currentUser?: any;
  onNavigate?: (path: string) => void;
}

interface DocSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: string[];
  bullets?: string[];
  callout?: {
    type: "info" | "warning" | "success" | "pro";
    text: string;
    actionLabel?: string;
    actionId?: string;
  };
}

export default function DocumentationPage({
  onUpgradeClick,
  onOpenSettingsClick,
  onOpenLegalTab,
  onMenuClick,
  isProUser = false,
  onContactSupportClick,
  currentUser,
  onNavigate
}: DocumentationPageProps) {
  const { t } = useTranslation(["common"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("getting-started");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Documentation sections content defined as structured data
  const docSections = useMemo<DocSection[]>(() => [
    {
      id: "getting-started",
      title: "Getting Started with BizNearYou",
      icon: <Compass className="w-5 h-5 text-blue-500" />,
      content: [
        "Welcome to BizNearYou! This documentation is designed to help job seekers, students, career changers, and employers get the most out of our map-driven local business directory and outreach CRM.",
        "To get started, follow these simple steps to discover and organize local workplace opportunities:",
      ],
      bullets: [
        "Create an Account: Click the 'Sign Up' button on the homepage to register. All your saved businesses, private notes, and pipeline progress are securely synced to your cloud account.",
        "Sign In: Use your registered email and password to access your personalized workspace.",
        "Enter a Location: Use the search header to type in a 5-digit postal ZIP code, a city, or neighborhood name to define your search area. You can also grant browser location permission to automatically search your immediate area.",
        "Specify Business Type: Type in a business category, keyword, industry, or role (such as 'dental practice', 'bakery', 'graphic design', or 'veterinarian') to filter search results.",
        "Review Map and List Results: Explore matches plotted on our high-density interactive map alongside a sidebar directory list showing distances, address details, and public-domain ratings.",
        "Open Listings & Save: Click any company to view its details, analyze activity markers, and save useful targets directly to your private CRM pipeline with a single click."
      ],
      callout: {
        type: "info",
        text: "Tip for Students: Exploring dental clinics, animal shelters, local design studios, or specialty trades is the absolute best way to locate unadvertised internship sites in your neighborhood.",
      }
    },
    {
      id: "search",
      title: "Searching for Businesses and Opportunities",
      icon: <SlidersHorizontal className="w-5 h-5 text-emerald-500" />,
      content: [
        "BizNearYou's search engine is optimized for geographical proximity and commercial relevance. Unlike traditional job boards, we index active operating businesses rather than just paid advertisements.",
        "When performing local search, please keep the following guidelines in mind:",
      ],
      bullets: [
        "Location Resolution: The search center pin defaults to your current geolocated coordinate (if allowed) or resolves center coordinates for any typed ZIP code or city.",
        "Search Radius & Filters: Refine your search using categories, average ratings, and proximity parameters. If you see too few listings, consider expanding your search radius or using a broader industry keyword.",
        "Resetting Filters: If you wish to start over, click 'Clear Filters' to restore the default wide search parameters.",
        "Information Availability: We compile business data from verified public registries and open web indexes. If a business does not publish a website, telephone line, or open contact email publicly, those fields will display as unavailable in our system.",
        "Hiring & Opportunity Context: BizNearYou helps you discover busy, thriving neighborhood operations, but does not guarantee that every business displayed is actively hiring. Instead, we identify targets where professional, direct talent inquiries are highly welcome and effective."
      ],
      callout: {
        type: "warning",
        text: "Please remember to verify hours of operation, physical address, and contact preferences directly with each business before visiting or calling.",
      }
    },
    {
      id: "map",
      title: "Using the Map Interface",
      icon: <Map className="w-5 h-5 text-indigo-500" />,
      content: [
        "Our interactive map is a core feature designed to let you visualize your local employer landscape geographically.",
        "The map and directory list are dynamically linked so that you never lose your context:",
      ],
      bullets: [
        "Geographic Markers: Every matching business is plotted with a custom coordinate pin on the map. Color-coded markers distinguish sponsored promotions or highly active signal zones.",
        "Marker Selection: Clicking a marker zooms the map to that coordinate, highlights the corresponding listing in your directory list, and opens the detailed information panel.",
        "Map Controls: Use standard pinch-to-zoom gestures, mouse scrolls, or the on-screen zoom buttons (+/-) to navigate. Click the 'Recenter' button to quickly snap back to your initial search location.",
        "Dynamic Sync: As you pan or move the map, search parameters are preserved. We strictly avoid introducing a separate, duplicate search system to ensure your map and sidebar results are always in perfect sync.",
        "Routing and Directions: Click 'Get Directions' inside any business detail card to open the coordinates in external navigation software for route distance and travel time estimates."
      ]
    },
    {
      id: "listing-details",
      title: "Understanding Business Listing Details",
      icon: <FileText className="w-5 h-5 text-violet-500" />,
      content: [
        "When you open a business profile, our system aggregates key operational details to help you assess whether they are a promising target.",
        "Each detailed listing card can include the following public-domain attributes:",
      ],
      bullets: [
        "Core Attributes: Registered company name, full street address, direct distance from your search center, and primary business category.",
        "Contact Lines: Active website URL (clickable) and direct office telephone number.",
        "Public Reviews: Average rating score and total review count. Steady, recent reviews are an excellent leading indicator of consumer activity.",
        "Hiring Signal Level: A calculated rating (Low, Medium, or High) based on public digital upgrades, review velocity, and website activity indicators.",
        "Pipeline Integration: Displays whether the listing is saved in your CRM and allows you to assign notes, follow-up dates, or custom outreach labels."
      ],
      callout: {
        type: "info",
        text: "Data Transparency: Our system compiles data strictly from public indexes. We do not display private credentials, private employee profiles, or confidential internal company data.",
      }
    },
    {
      id: "saved-businesses",
      title: "Saved Businesses and CRM Dashboard",
      icon: <ShieldCheck className="w-5 h-5 text-amber-500" />,
      content: [
        "To save businesses and start organizing your direct outreach, simply click the 'Save' button or heart icon on any company card. Saved businesses are instantly added to your personal CRM workspace.",
        "Our professional Kanban Pipeline enables you to track your outreach progress across standard stages:",
      ],
      bullets: [
        "Prospect: Businesses you are currently researching or planning to contact.",
        "Contacted: Outbound introductions sent via email, phone, or in person.",
        "Followed Up: Respectful subsequent contact completed after 5-7 business days.",
        "Interviewing: Active discussions or meetings scheduled with the owner or manager.",
        "Hired: Direct outreach succeeded and an opportunity or role was secured!",
        "Managing Progress: Assign private notes, set custom follow-up dates, or track contact history directly inside each saved business file to stay organized."
      ],
      callout: {
        type: "pro",
        text: "Pro Features: Upgrade your account to unlock unlimited saved businesses, bulk CSV spreadsheet export tools, and priority local search alerts.",
        actionLabel: "Upgrade to Pro",
        actionId: "upgrade"
      }
    },
    {
      id: "account",
      title: "Account and Profile Management",
      icon: <User className="w-5 h-5 text-rose-500" />,
      content: [
        "Your BizNearYou account secures your data and makes it accessible across all desktop, tablet, and mobile browsers.",
        "Manage your account preferences easily via the Settings panel:",
      ],
      bullets: [
        "Authentication: Securely log in and out using your authenticated email.",
        "Profile Details: Keep your contact information, resume preferences, and target categories updated to receive optimal matches.",
        "Account Recovery: If you forget your password, click 'Forgot Password' on the sign-in screen to receive a secure, private reset link via email.",
        "Notification Preferences: Configure your email preferences for job alerts, pipeline reminders, or newsletter tips.",
        "Account Deletion: If you ever decide to deactivate, our support team can assist with permanent removal of your account, saved pipelines, and associated historical files."
      ]
    },
    {
      id: "pricing",
      title: "Free and Paid Pro Features",
      icon: <CreditCard className="w-5 h-5 text-teal-500" />,
      content: [
        "BizNearYou is committed to keeping core tools accessible for student job seekers and neighborhood communities. We offer transparent plans with no hidden fees:",
      ],
      bullets: [
        "Free Tier: Full access to nearby business searches, interactive map routing, pipeline tracking (up to 15 saved companies), and basic directory details.",
        "Pro Plan: Adds unlimited saved businesses, bulk CSV spreadsheet exports, automated weekly search scanners, and priority CRM pipeline tools.",
        "Billing & Cancellation: Subscriptions are billed on a flexible monthly or yearly cycle. You can cancel your subscription anytime through the billing settings. Upon cancellation, your account returns to the Free tier and your saved pipeline data is fully preserved.",
        "Refund Policy: Because we offer a robust free tier to test the platform completely, refunds are reviewed on a case-by-case basis by reaching out to our support team."
      ],
      callout: {
        type: "info",
        text: "Stripe Billing Simulation: All payments in our preview and sandbox environments are fully simulated. You can test upgrading using simulated test credentials without incurring real charges.",
      }
    },
    {
      id: "employers",
      title: "For Employers and Businesses",
      icon: <Sparkles className="w-5 h-5 text-indigo-500" />,
      content: [
        "Are you a local business owner or recruiter looking to hire nearby talent? Switch your account to 'Employer Mode' inside the Career Hub to post active roles and promote your workplace.",
        "We support local business promotion through simple, transparent tools:",
      ],
      bullets: [
        "Active Postings: Design clean recruitment posts featuring pay ranges, skills, and direct application methods.",
        "Sponsored Campaigns: Launch targeted geofenced campaigns. Specify target ZIP codes, categories, and CPC (Cost-Per-Click) bids to highlight your opportunities to nearby candidates.",
        "Ad Transparency: Sponsored listings are clearly labeled with 'Sponsored' badges. Candidate tracking respects all privacy settings and user 'Do Not Track' flags.",
        "Listing Safety: We prohibit misleading, discriminatory, or fraudulent postings. Job seekers can flag any suspicious listing for immediate review and removal."
      ]
    },
    {
      id: "privacy-safety",
      title: "Privacy, Data Rights, and Safety Guidance",
      icon: <ShieldCheck className="w-5 h-5 text-emerald-500" />,
      content: [
        "We treat your personal information with the highest standard of privacy and care. Read our complete policies to learn more:",
      ],
      bullets: [
        "Data Storage: We only save account details, saved directory listings, private notes, and custom seeker parameters. Your precise, live browser location coordinates are never stored on our servers.",
        "Third-Party Listings: Business directory records are compiled solely from public-domain commercial indexes. We respect corporate preferences and honor profile removal requests immediately.",
        "Direct Outreach Safety: While direct outreach is highly successful, protect your safety. Never pay an individual to receive a job offer, avoid sharing sensitive financial credentials (like SSNs or bank logins) early in communications, and conduct initial meetings in public or verified company locations.",
        "Reporting Violations: Use the report controls or contact us directly to report fraudulent listings, scam behavior, or inaccurate data."
      ],
      callout: {
        type: "pro",
        text: "Read Policies: You can access our full Privacy Policy and Terms of Service via the footer links at the bottom of any page.",
      }
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting Common Issues",
      icon: <ShieldAlert className="w-5 h-5 text-rose-500" />,
      content: [
        "Encountering a technical issue? Check these common solutions before contacting support:",
      ],
      bullets: [
        "Search Returns No Results: Verify your keyword spelling. Try expanding your search radius, manually entering a neighboring city or ZIP code, or using a broader category.",
        "Location Permission Denied: If the map fails to detect your location, enter a postal ZIP code or city name in the input header to center the map manually. You can reset permission states in your browser settings.",
        "Map Not Loading: Ensure ad-blockers or security extensions are not blocking Google Maps or Mapbox scripts. Refreshing your browser usually re-initializes the viewport.",
        "Incomplete Business Details: Many local offices choose not to list a phone number or website. This is an excellent opportunity to introduce yourself in person during quiet commercial hours.",
        "Saved listings Missing: Ensure you are logged in to the correct account. Guest or non-logged-in saves are stored temporarily in your local browser cache and can be lost if cache is cleared."
      ],
      callout: {
        type: "info",
        text: "Need a Fresh Start? If the dashboard appears unresponsive, log out, clear your browser cookies/cache, and sign back in to reload your profile.",
      }
    },
    {
      id: "support",
      title: "Support, Bug Reporting, and Feedback",
      icon: <HelpCircle className="w-5 h-5 text-blue-500" />,
      content: [
        "We are here to ensure your local search and outreach pipeline runs smoothly.",
        "If you need to report a technical bug, request data correction, or query billing, use our official contact channels:",
      ],
      bullets: [
        "Submit a Ticket: Visit our Contact page to send a direct message. Choose 'Technical Support', 'Billing', or 'Data Correction / Removal Request' as your topic.",
        "Information to Include: To speed up resolution, please provide your operating system/browser, the specific business name or coordinate involved, and a clear description of the issue.",
        "Business Profile Removal: If you are an owner wishing to remove your company index listing, submit your request via the Contact page. Removal is prioritized and completed within 48 business hours."
      ],
      callout: {
        type: "success",
        text: "Response Standards: All support requests are reviewed by our engineering team, with direct responses sent within 24 to 48 business hours.",
      }
    }
  ], [isProUser]);

  // FAQ structured data containing exactly 30 questions matching requirements
  const faqItems = useMemo(() => [
    {
      q: "What is BizNearYou?",
      a: "BizNearYou is an interactive, map-driven local business research platform and private outreach CRM designed for starting professionals, students, and proactive job seekers to identify nearby employers, analyze activity markers, and manage direct communications safely."
    },
    {
      q: "Is BizNearYou a job board?",
      a: "No. BizNearYou is not a traditional job board. Regular job boards only list active, advertised job openings which attract hundreds of competing applicants. Instead, BizNearYou is a direct discovery research tool that helps you target active, busy neighborhood operations to access the unadvertised 'hidden' local job market."
    },
    {
      q: "Is BizNearYou free?",
      a: "Yes! BizNearYou offers a comprehensive free tier that lets you search nearby commercial targets, use the interactive map, and manage up to 15 saved companies in your private CRM. Optional paid Pro memberships are available for advanced search filters, spreadsheet exports, and unlimited pipeline organization."
    },
    {
      q: "Do I need an account to search?",
      a: "No. You can run basic local business searches and explore nearby opportunities on the map without an account. However, you will need to sign up for a free account to save listings, write private notes, set reminders, or use your private Kanban board."
    },
    {
      q: "Do I need an account to save businesses?",
      a: "Yes. An account is required to save businesses so that your pipeline records, communication history, and private notes can be securely associated with your profile and synced across all your devices."
    },
    {
      q: "How does the location-based search work?",
      a: "Our search matches your query with high-quality regional directories. Enter a location (like a ZIP code, city, or neighborhood) and an industry keyword. The engine calculates coordinates, plots pins on your map within your preferred radius, and shows distances from your location center."
    },
    {
      q: "Can I search by city or ZIP code?",
      a: "Yes! You can search by typing in any valid city name, state abbreviation, or 5-digit ZIP code. The search resolves coordinates instantly to plot high-density employer clusters near that geographical center."
    },
    {
      q: "Can I search for a specific industry or job type?",
      a: "Yes. You can filter results by typing specialized business categories (e.g., 'Dental Office', 'Graphic Design Studio', 'Veterinary Practice', or 'Auto Repair Shop') or keywords to match your background and career goals."
    },
    {
      q: "Does every business shown on BizNearYou have an open job?",
      a: "No. BizNearYou helps you map nearby operations but does not guarantee that every business currently has an open vacancy. We prioritize showing you active, busy companies that are great candidates for proactive talent introductions before they list roles elsewhere."
    },
    {
      q: "How do I know whether a business is currently hiring?",
      a: "We analyze multiple public-domain activity signals—such as positive Google review velocity, digital web tool updates, and local promotions—to assign a hiring likelihood score. A strong score suggests the business is thriving and likely understaffed."
    },
    {
      q: "Where does the business information come from?",
      a: "Our directory listings are compiled from public registries, local business directories, and open commercial web indexes. We collect map coordinates, public review patterns, and website meta tags to help you discover local employers."
    },
    {
      q: "Why is some business information missing?",
      a: "Data completeness depends entirely on what each business publishes publicly. If an office does not maintain a registered telephone line, website, or active profile in public directories, those fields will display as empty in our workspace."
    },
    {
      q: "How do I report incorrect business information?",
      a: "If you spot out-of-date details, closed locations, or incorrect phone numbers, please visit our Contact page, select the 'Feedback / Data Correction' topic, and submit the details. We review and correct our database within 48 business hours."
    },
    {
      q: "How do I save a business?",
      a: "Simply click the 'Save' button or heart icon on any company card in your search list, or click on a marker on the map and save it from the detail popover. This copies the company profile directly into your dashboard."
    },
    {
      q: "Where can I find my saved businesses?",
      a: "All saved listings appear in your private CRM Dashboard, accessible via the 'Saved Businesses' tab in the sidebar menu. From there, you can view your pipeline stages, schedule callback sessions, and log communication notes."
    },
    {
      q: "How does the map work?",
      a: "The map plots your search results geographically. Clicking a marker centers the map, highlights that business in your sidebar list, and opens its detail card. Zooming or panning naturally updates the density of visible pins."
    },
    {
      q: "Why is the map not loading?",
      a: "Map load issues are usually caused by browser extensions or ad-blockers blocking standard Google Maps or Mapbox scripts, or denied browser location permissions. Disabling extensions and refreshing the page typically fixes it."
    },
    {
      q: "Does BizNearYou track my precise location?",
      a: "No. Browser location permissions are only requested to center the map on your current area for convenience. Your precise coordinates are never stored on our servers, tracked in the background, or shared with third parties."
    },
    {
      q: "Can employers advertise opportunities on BizNearYou?",
      a: "Yes. Local business owners can switch to 'Employer Mode' inside the Career Hub to build a corporate profile, publish active job vacancies, or launch local geofenced sponsored campaigns to attract nearby talent."
    },
    {
      q: "How are sponsored or promoted opportunities labeled?",
      a: "To ensure absolute transparency, all promoted company pages or sponsored job posts are clearly labeled with a prominent 'Sponsored' or 'Promoted' badge inside candidate search feeds."
    },
    {
      q: "How do I upgrade my account?",
      a: "Select the 'Upgrade to Pro' or 'Billing' options in your sidebar menu or dashboard. This opens our billing center where you can select a subscription tier and complete payment via our secure Stripe checkout simulator."
    },
    {
      q: "How do I cancel a paid plan?",
      a: "You can cancel your subscription anytime in your Account Settings. Clicking 'Manage Subscription' lets you disable auto-renewal with one click. Your account will remain active until the end of the current billing cycle."
    },
    {
      q: "Will cancelling delete my saved businesses?",
      a: "No. Cancelling your paid subscription simply downgrades your account to the Free tier. Your saved businesses, pipeline progress, callback schedules, and private notes will remain completely intact."
    },
    {
      q: "How do I reset my password?",
      a: "Go to the Login screen, click the 'Forgot Password' link, and enter your registered email address. We will send a secure password reset link to recover your account instantly."
    },
    {
      q: "How do I contact BizNearYou support?",
      a: "You can submit support requests through our unified Contact Page by choosing the appropriate topic (e.g. Technical Support, Billing, or Feedback). Our support team will respond to your registered email within 24 to 48 business hours."
    },
    {
      q: "How can I report a scam or suspicious opportunity?",
      a: "If you spot a listing that appears fraudulent, requests payment to work, or looks like a scam, click the flag icon or contact us via our support form. We audit and remove flagged listings immediately to ensure candidate safety."
    },
    {
      q: "Is BizNearYou available on mobile devices?",
      a: "Yes! The entire platform is built with mobile-first responsiveness, featuring touch-friendly buttons, custom swipe gestures, and collapsible map-list workspaces for smartphones and tablets."
    },
    {
      q: "Can students use BizNearYou to find internships or entry-level opportunities?",
      a: "Absolutely! BizNearYou is highly popular with high school and college students looking for entry-level work, trade apprenticeships, or local internship sites that are rarely advertised on competitive global job boards."
    },
    {
      q: "Can I contact businesses directly?",
      a: "Yes, direct professional communication is highly encouraged! We provide public company website links, telephone numbers, and tailored outreach pitch suggestions to help you initiate respectful contact."
    },
    {
      q: "Does BizNearYou guarantee that I will receive a job?",
      a: "No. BizNearYou is a professional research directory and outbound CRM tool. We do not hire candidates directly, act as an employment agency, or guarantee interviews or callbacks. Your success is driven by your direct proactive outreach."
    }
  ], []);

  // Handle section clicking with smooth scrolling and update URL hash
  const scrollToSection = (id: string) => {
    setActiveSection(id);
    setIsMobileMenuOpen(false);
    
    // Push hash to history safely without page reload
    window.history.pushState(null, "", `#${id}`);

    const element = document.getElementById(id);
    if (element) {
      // Find offset to account for sticky header
      const headerOffset = 90;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Scroll Spy to highlight active section on scroll
  useEffect(() => {
    const handleScrollSpy = () => {
      // Manage "Back to Top" visibility
      setShowBackToTop(window.scrollY > 400);

      // Determine active section
      const scrollPosition = window.scrollY + 120;
      const currentActive = docSections.find((section) => {
        const el = document.getElementById(section.id);
        if (!el) return false;
        const top = el.offsetTop;
        const height = el.offsetHeight;
        return scrollPosition >= top && scrollPosition < top + height;
      });

      if (currentActive && currentActive.id !== activeSection) {
        setActiveSection(currentActive.id);
      }
    };

    window.addEventListener("scroll", handleScrollSpy, { passive: true });
    return () => window.removeEventListener("scroll", handleScrollSpy);
  }, [activeSection, docSections]);

  // Initial Hash Loading and hashchange support
  useEffect(() => {
    const handleInitialHash = () => {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.replace("#", "");
        // If it's "faq", open the first item
        if (id === "faq") {
          setOpenFaq(0);
        }
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            const headerOffset = 100;
            const elPosition = el.getBoundingClientRect().top;
            const offsetPosition = elPosition + window.pageYOffset - headerOffset;
            window.scrollTo({ top: offsetPosition, behavior: "smooth" });
            setActiveSection(id);
          }
        }, 150);
      }
    };

    // Delay slightly to ensure layout rendering completes
    const timer = setTimeout(handleInitialHash, 200);
    window.addEventListener("hashchange", handleInitialHash);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("hashchange", handleInitialHash);
    };
  }, []);

  // Filter sections and FAQs based on search query
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return { sections: docSections, faqs: faqItems };
    }

    const filteredSections = docSections.filter(sec => {
      const titleMatch = sec.title.toLowerCase().includes(query);
      const contentMatch = sec.content.some(p => p.toLowerCase().includes(query));
      const bulletMatch = sec.bullets?.some(b => b.toLowerCase().includes(query)) || false;
      return titleMatch || contentMatch || bulletMatch;
    });

    const filteredFaqs = faqItems.filter(item => {
      const qMatch = item.q.toLowerCase().includes(query);
      const aMatch = item.a.toLowerCase().includes(query);
      return qMatch || aMatch;
    });

    return {
      sections: filteredSections,
      faqs: filteredFaqs
    };
  }, [searchQuery, docSections, faqItems]);

  const totalResultsCount = searchResults.sections.length + searchResults.faqs.length;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans flex flex-col relative" id="docs-parent-wrapper">
      {/* Dynamic Breadcrumbs Structured Data JSON-LD & Navigation */}
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <SeoBreadcrumbs items={[{ label: "Documentation", path: "/docs" }]} onNavigate={onNavigate || (() => {})} />
      </div>

      {/* Dynamic FAQPage Structured Data JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map((item) => ({
              "@type": "Question",
              "name": item.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.a
              }
            }))
          })
        }}
      />

      {/* Hero / Header Section */}
      <div className="bg-white border-b border-slate-200 py-10 px-6 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2 text-left">
              <div className="flex items-center gap-2">
                <span className="p-1.5 bg-blue-50 text-blue-600 rounded-lg border border-blue-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5" />
                </span>
                <span className="text-xs font-bold tracking-wider text-blue-600 uppercase">Knowledge Base</span>
              </div>
              <h1 className="text-3xl font-black text-slate-900 tracking-tight">BizNearYou Documentation</h1>
              <p className="text-slate-500 text-sm max-w-2xl leading-relaxed">
                Discover nearby businesses, analyze localized hiring signals, manage direct professional outreach, utilize the interactive map tracking CRM, and resolve common queries.
              </p>
            </div>

            {/* Quick Navigation Action Buttons */}
            <div className="flex flex-wrap items-center gap-3">
              {onNavigate && (
                <button
                  onClick={() => onNavigate("/")}
                  className="px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-2xs"
                >
                  <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                  <span>Back to Homepage</span>
                </button>
              )}

              {currentUser ? (
                onNavigate && (
                  <button
                    onClick={() => onNavigate("/dashboard")}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-sm hover:shadow"
                  >
                    <span>Go to My Dashboard</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )
              ) : (
                onNavigate && (
                  <button
                    onClick={() => onNavigate("/signup")}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-sm hover:shadow"
                  >
                    <span>Create Free Account</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )
              )}
            </div>
          </div>

          {/* Search Box Panel */}
          <div className="pt-2">
            <div className="relative flex items-center w-full max-w-xl">
              <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                aria-label="Search documentation and FAQ"
                placeholder="Search topics, keywords, or FAQ questions (e.g. 'Stripe', 'hiring', 'resume')..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-slate-200 rounded-2xl pl-10 pr-10 py-3.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 hover:bg-slate-100/50 transition-colors shadow-2xs"
              />
              {searchQuery && (
                <button 
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-3.5 p-1 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition cursor-pointer"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {searchQuery && (
              <p className="text-xs font-medium text-slate-500 mt-2">
                Found {totalResultsCount} matching results for "{searchQuery}"
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Documentation Page Content Grid */}
      <div className="max-w-7xl w-full mx-auto flex-1 px-6 sm:px-8 py-8 flex flex-col lg:flex-row gap-8 items-start">
        
        {/* Desktop Sticky Table of Contents Sidebar */}
        {!searchQuery && (
          <aside className="w-64 shrink-0 sticky top-24 hidden lg:block bg-white border border-slate-200 rounded-2xl p-4 shadow-3xs text-left">
            <h3 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-3 mb-3">On This Page</h3>
            <nav className="space-y-1" aria-label="Documentation navigation">
              {docSections.map((section) => {
                const isSelected = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
                      isSelected
                        ? "bg-blue-600 text-white font-extrabold shadow-sm pl-4"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    <span>{section.title}</span>
                  </button>
                );
              })}
              {/* FAQ Navigation Link */}
              <button
                onClick={() => scrollToSection("faq")}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
                  activeSection === "faq"
                    ? "bg-blue-600 text-white font-extrabold shadow-sm pl-4"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <span>Frequently Asked Questions (FAQ)</span>
              </button>
            </nav>

            {/* Quick Help Centre Links */}
            <div className="mt-6 pt-6 border-t border-slate-100 text-xs space-y-3 px-1">
              <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Resources</h4>
              {onOpenSettingsClick && (
                <button 
                  onClick={onOpenSettingsClick}
                  className="flex items-center gap-2 text-slate-600 hover:text-blue-600 cursor-pointer font-bold transition"
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>Configure Map Styles</span>
                </button>
              )}
              {onUpgradeClick && (
                <button 
                  onClick={onUpgradeClick}
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 cursor-pointer font-extrabold transition"
                >
                  <CreditCard className="w-3.5 h-3.5 text-indigo-500" />
                  <span>Billing / Upgrade Plan</span>
                </button>
              )}
              {onContactSupportClick && (
                <button 
                  onClick={onContactSupportClick}
                  className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 cursor-pointer font-bold transition"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                  <span>Contact Tech Support</span>
                </button>
              )}
            </div>
          </aside>
        )}

        {/* Mobile Navigation Dropdown Menu (Sticky Float) */}
        {!searchQuery && (
          <div className="w-full lg:hidden shrink-0 sticky top-18 z-40 bg-white border border-slate-200 rounded-xl shadow-xs p-3 flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">Navigation:</span>
            <div className="relative">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-extrabold flex items-center gap-1.5 cursor-pointer text-slate-800 hover:bg-slate-100"
              >
                <span>
                  {activeSection === "faq" 
                    ? "Frequently Asked Questions" 
                    : docSections.find(s => s.id === activeSection)?.title || "Select Section"
                  }
                </span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>

              {isMobileMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg py-2 z-50 text-left max-h-72 overflow-y-auto">
                  {docSections.map((sec) => (
                    <button
                      key={sec.id}
                      onClick={() => scrollToSection(sec.id)}
                      className={`w-full text-left px-4 py-2 text-xs font-semibold ${
                        activeSection === sec.id ? "bg-blue-50 text-blue-700 font-bold" : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {sec.title}
                    </button>
                  ))}
                  <button
                    onClick={() => scrollToSection("faq")}
                    className={`w-full text-left px-4 py-2 text-xs font-semibold border-t border-slate-100 ${
                      activeSection === "faq" ? "bg-blue-50 text-blue-700 font-bold" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    Frequently Asked Questions (FAQ)
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Core Documents and FAQ Content Area */}
        <div className="flex-1 w-full space-y-12 text-left">
          
          {/* Documentation Sections */}
          <div className="space-y-12">
            {searchResults.sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                data-doc-section
                className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-3xs space-y-5 scroll-mt-24 transition-all hover:border-slate-300"
              >
                <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                  <span className="p-2 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-center">
                    {section.icon}
                  </span>
                  <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-4">
                  {section.content.map((p, pIdx) => (
                    <p key={pIdx} className="text-sm text-slate-600 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>

                {section.bullets && (
                  <ul className="space-y-2.5 text-sm text-slate-600 pl-5 list-disc leading-relaxed">
                    {section.bullets.map((b, bIdx) => {
                      // Format bold label prefixes if colon present
                      const colonIdx = b.indexOf(":");
                      if (colonIdx !== -1) {
                        const boldPart = b.substring(0, colonIdx + 1);
                        const normalPart = b.substring(colonIdx + 1);
                        return (
                          <li key={bIdx}>
                            <strong className="text-slate-800 font-extrabold">{boldPart}</strong>
                            {normalPart}
                          </li>
                        );
                      }
                      return <li key={bIdx}>{b}</li>;
                    })}
                  </ul>
                )}

                {section.callout && (
                  <div className={`p-4 rounded-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6 ${
                    section.callout.type === "warning"
                      ? "bg-amber-50 border-amber-200 text-amber-900"
                      : section.callout.type === "success"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : section.callout.type === "pro"
                      ? "bg-indigo-50 border-indigo-200 text-indigo-900"
                      : "bg-blue-50 border-blue-200 text-blue-900"
                  }`}>
                    <div className="flex items-start gap-3 text-left">
                      {section.callout.type === "warning" ? (
                        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                      ) : (
                        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                      )}
                      <p className="text-xs font-semibold leading-relaxed">
                        {section.callout.text}
                      </p>
                    </div>
                    {section.callout.actionLabel && (
                      <button
                        onClick={() => {
                          if (section.callout?.actionId === "upgrade" && onUpgradeClick) {
                            onUpgradeClick();
                          }
                        }}
                        className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-black px-4 py-2 rounded-lg shadow-2xs cursor-pointer transition shrink-0"
                      >
                        {section.callout.actionLabel}
                      </button>
                    )}
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* FAQ Accordion Section */}
          {(searchResults.faqs.length > 0) && (
            <section
              id="faq"
              data-doc-section
              className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-3xs space-y-6 scroll-mt-24 transition-all"
            >
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                <span className="p-2 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-indigo-500" />
                </span>
                <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">
                  Frequently Asked Questions (FAQ)
                </h2>
              </div>

              <p className="text-slate-500 text-sm leading-relaxed">
                Find clear, accurate answers to common questions about BizNearYou's business research, map directories, search parameters, plans, and technical assistance.
              </p>

              {/* Accordion List */}
              <div className="space-y-3.5" role="presentation">
                {searchResults.faqs.map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div 
                      key={idx} 
                      className="border border-slate-100 rounded-xl overflow-hidden transition-colors bg-slate-50/50 hover:bg-slate-50 hover:border-slate-200"
                    >
                      <button
                        id={`faq-btn-${idx}`}
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={`faq-content-${idx}`}
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                        className="w-full flex items-center justify-between p-4.5 text-left font-bold text-slate-900 text-xs sm:text-sm hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition cursor-pointer"
                      >
                        <span className="pr-4">{faq.q}</span>
                        <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 motion-reduce:transition-none ${isOpen ? "rotate-90 text-blue-600" : ""}`} />
                      </button>
                      
                      {isOpen && (
                        <div
                          id={`faq-content-${idx}`}
                          role="region"
                          aria-labelledby={`faq-btn-${idx}`}
                          className="px-4.5 pb-4.5 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100 bg-white"
                        >
                          <p>{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* No Search Results Fallback */}
          {totalResultsCount === 0 && (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 max-w-md mx-auto p-6 space-y-4 shadow-3xs">
              <Info className="w-12 h-12 text-slate-300 mx-auto animate-pulse" />
              <h3 className="text-sm font-extrabold text-slate-800">No matching documentation or FAQ found</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                We couldn't find any documentation sections or frequently asked questions matching "{searchQuery}". Try refining your terms or search using common keywords.
              </p>
              <div className="flex justify-center pt-2">
                <button
                  onClick={() => setSearchQuery("")}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 text-xs font-bold rounded-xl transition cursor-pointer"
                >
                  Reset Search Filter
                </button>
              </div>
            </div>
          )}

          {/* Contact Support Direct Action Block */}
          <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-2xl p-6 sm:p-8 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 border border-slate-200 dark:border-slate-800">
            <div className="space-y-2 text-left">
              <h3 className="font-extrabold text-base flex items-center gap-2 text-slate-900 dark:text-white">
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
                Still need help or have a data correction inquiry?
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl">
                Our customer support and data operations team are here to assist. For profile removal requests, incorrect address edits, map discrepancies, or business account billing help, submit a secure support request.
              </p>
            </div>
            
            {onContactSupportClick && (
              <button
                onClick={onContactSupportClick}
                className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs px-5 py-2.5 rounded-xl shadow-xs transition cursor-pointer shrink-0 self-start md:self-center hover:scale-[1.02]"
              >
                Contact Support Team
              </button>
            )}
          </div>

          {/* Bottom Legal Quick Links */}
          {onOpenLegalTab && (
            <div className="border border-slate-200 bg-white p-5 rounded-2xl space-y-3 shadow-3xs">
              <h4 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                Legal Documents and Disclosures
              </h4>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                BizNearYou is committed to total privacy compliance and data safety. Select from our published policies below for comprehensive regulatory terms, and acceptable use guidelines:
              </p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] font-bold text-slate-600">
                <button onClick={() => onOpenLegalTab("terms")} className="hover:text-blue-600 underline cursor-pointer">Terms of Service</button>
                <span className="text-slate-300">•</span>
                <button onClick={() => onOpenLegalTab("privacy")} className="hover:text-blue-600 underline cursor-pointer">Privacy Policy</button>
                <span className="text-slate-300">•</span>
                <button onClick={() => onOpenLegalTab("acceptable-use")} className="hover:text-blue-600 underline cursor-pointer">Acceptable Use Guideline</button>
                <span className="text-slate-300">•</span>
                <button onClick={() => onOpenLegalTab("contact-removal")} className="hover:text-rose-600 underline text-rose-700 cursor-pointer">Profile Removal Request</button>
                <span className="text-slate-300">•</span>
                <button onClick={() => onOpenLegalTab("sponsored-jobs")} className="hover:text-blue-600 underline cursor-pointer">Sponsorship Disclosure</button>
                <span className="text-slate-300">•</span>
                <button onClick={() => onOpenLegalTab("safety")} className="hover:text-rose-600 underline text-rose-700 cursor-pointer">Candidate Safety Center</button>
                <span className="text-slate-300">•</span>
                <button onClick={() => onOpenLegalTab("refunds")} className="hover:text-blue-600 underline cursor-pointer">Refund Policy</button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Floating Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 p-3 bg-slate-900 text-white rounded-full shadow-lg hover:bg-slate-800 hover:scale-105 transition cursor-pointer z-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Back to Top"
          title="Back to Top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
